import { api } from "../lib/api";
import type { OpenInvoiceSummary, QueueClient } from "../components/messages/clientQueue";

// PKT-0581 step 3 · the fetch behind the Client Action Queue.
//
// GET /api/clients/queue (backend/api/routes/client_queue.py) returns one row
// per non-deleted client with every field clientQueue.ts's buildQueue needs —
// field names match the QueueClient interface 1:1, pinned on both sides:
// backend/tests/test_client_queue_route.py and
// components/messages/__tests__/clientQueue.test.ts.
//
// `generated_at` is the backend's clock at assembly time. Pass it to
// buildQueue as nowIso so ranking is deterministic against the data's own
// timestamps — the frontend's clock never enters the sort.

// Ch76: OpenInvoiceSummary moved into clientQueue.ts — the queue model now
// renders and ranks on open_invoice, so the model owns the type. Re-exported
// here so existing importers keep working.
export type { OpenInvoiceSummary };

/** One wire row. QueueClient covers the scorer + conversation facts +
 * open_invoice (rendered and ranked on since Ch76); the extras below ride
 * along for future panes and are ignored by buildQueue today. */
export interface ClientQueueRow extends QueueClient {
  last_shortlist_engaged_at?: string | null;
  unanswered_inbound?: boolean;
}

/** Ch78 · whose clients a Messages read is about. "mine" (default) · "all" ·
 * an agent's profile id. Non-switching roles are forced to "mine" server-side
 * and the response's `scope` says so. */
export type QueueAgentScope = "mine" | "all" | string;

export interface QueueScopeInfo {
  mode: "mine" | "all" | "agent";
  agent_id: string | null;
  can_switch: boolean;
}

export interface QueueAgentOption {
  id: string;
  full_name: string;
  email?: string | null;
}

export interface ClientQueueResponse {
  generated_at: string;
  clients: ClientQueueRow[];
  /** Absent on a backend that predates Ch78 — treated as "mine, no switcher". */
  scope?: QueueScopeInfo;
  agents?: QueueAgentOption[];
  dismissal_available?: boolean;
}

export interface DismissClientQueueRow {
  last_inbound_id: string | null;
  last_inbound_tie_fingerprint: string | null;
  last_inbound_at: string | null;
  move_in_timeline: string | null;
  move_in_date: string | null;
  move_in_near: boolean;
}

export async function dismissClientQueueRow(clientId: string, body: DismissClientQueueRow, agent: QueueAgentScope = "mine"): Promise<void> {
  await api.post(`/clients/queue/${encodeURIComponent(clientId)}/dismiss${agentQuery(agent)}`, body);
}

/** The query string for a scope; "mine" is the server default and sends nothing. */
export function agentQuery(agent: QueueAgentScope | undefined): string {
  return agent && agent !== "mine" ? `?agent=${encodeURIComponent(agent)}` : "";
}

export async function fetchClientQueue(agent: QueueAgentScope = "mine"): Promise<ClientQueueResponse> {
  // Ch83 · waits out a deploy restart rather than handing the operator a red
  // box for something about to succeed. This is the FIRST call a Messages
  // session makes, so it is the one that meets a restarting API.
  const res = await api.get<ClientQueueResponse>(`/clients/queue${agentQuery(agent)}`, {
    retry: true,
  });
  // PR #454 review Q1 (BLOCKING): the service is the shape guard. A 200 whose
  // body is not the contract (proxy/CDN error body, frontend deployed ahead of
  // client_queue.py, future reshaping) must become a REJECTION here so the
  // page's existing catch renders the certified queue-error state. Without
  // this, `queue.clients.find` threw during render — and the app has NO
  // error boundary at any level, so the whole tree unmounted to a white page.
  // Until a route-level ErrorBoundary exists (being minted as its own
  // packet), this validation IS the guard.
  if (!res || !Array.isArray((res as any).clients)) {
    throw new Error("Malformed queue response: `clients` is not an array");
  }
  for (const row of (res as any).clients) {
    if (typeof row !== "object" || row === null) {
      throw new Error("Malformed queue response: non-object client row");
    }
  }
  return res;
}
